import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { PLANS, formatNumber, WITHDRAWAL_FREQUENCIES } from '../utils/constants';
import '../styles/InvestmentsList.css';

const InvestmentsList = ({ 
  userInvestments, 
  stakingContract, 
  refreshData, 
  setLoading, 
  loading, 
  userInfo 
}) => {
  const [investmentROIData, setInvestmentROIData] = useState({});
  const [roiWithdrawalProcessing, setROIWithdrawalProcessing] = useState({});
  const [capitalWithdrawalProcessing, setCapitalWithdrawalProcessing] = useState({});
  const [planDetails, setPlanDetails] = useState({});
  const [capitalStatus, setCapitalStatus] = useState([]);
  const [notifications, setNotifications] = useState([]);

  // Load plan details from contract
  useEffect(() => {
    if (stakingContract) {
      loadPlanDetails();
    }
  }, [stakingContract]);

  // Load ROI data for each investment from contract
  useEffect(() => {
    if (stakingContract && userInfo && userInvestments.length > 0) {
      loadInvestmentROIFromContract();
      calculateCapitalStatus();
    }
  }, [stakingContract, userInfo, userInvestments, planDetails]);

  const addNotification = (message, type = 'info', duration = 5000) => {
    const id = Date.now();
    const notification = { id, message, type };
    
    setNotifications(prev => [...prev, notification]);
    
    if (duration > 0) {
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== id));
      }, duration);
    }
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const loadPlanDetails = async () => {
    if (!stakingContract) return;
    
    try {
      const plans = {};
      for (let i = 1; i <= 14; i++) {
        try {
          const planInfo = await stakingContract.getPlanInfo(i);
          plans[i] = {
            minAmount: planInfo[0],
            maxAmount: planInfo[1], 
            roiPercentage: planInfo[2],
            duration: planInfo[3],
            withdrawFrequency: planInfo[4],
            capitalWithdrawTime: planInfo[5],
            referralCommission: planInfo[6],
            requiresReferrals: planInfo[7],
            isActive: planInfo[8],
            capitalWithdrawAllowed: planInfo[9]
          };
        } catch (error) {
          console.error(`Failed to load plan ${i}:`, error);
        }
      }
      setPlanDetails(plans);
    } catch (error) {
      console.error('Failed to load plan details:', error);
    }
  };

  const getPlanInfo = (planId) => {
    return PLANS.find(plan => plan.id === parseInt(planId)) || null;
  };

  const isCapitalWithdrawAllowed = (planId) => {
    return [2, 3, 5, 6, 7, 8].includes(parseInt(planId));
  };

  const isEndOnlyPlan = (planId) => {
    return [1, 6, 7, 8, 9].includes(parseInt(planId));
  };

  const canWithdrawROI = (investment) => {
    const planId = parseInt(investment.planId);
    const planInfo = getPlanInfo(planId);
    
    if (!planInfo || !investment.isActive) return false;
    
    // For end-only plans, check if the duration has passed
    if (isEndOnlyPlan(planId)) {
      const now = Date.now();
      const startTime = investment.startTime.getTime();
      const durationMs = planId === 1 ? 60 * 30 * 24 * 60 * 60 * 1000 : // 60 months for plan 1
                        (planId === 6 || planId === 7) ? 36 * 30 * 24 * 60 * 60 * 1000 : // 36 months for plans 6,7
                        1825 * 24 * 60 * 60 * 1000; // 1825 days for plans 8,9
      
      return now >= (startTime + durationMs);
    }
    
    // For regular withdrawal plans, ROI can be withdrawn anytime
    return true;
  };

  const calculateCapitalStatus = async () => {
    if (!Object.keys(planDetails).length || !userInvestments.length) return;

    const statusArray = await Promise.all(
      userInvestments.map(async (inv, index) => {
        const planId = parseInt(inv.planId);
        
        if (inv.capitalWithdrawn || !inv.isActive) {
          return {
            index,
            isAvailable: false,
            timeRemaining: 0,
            status: inv.capitalWithdrawn ? 'withdrawn' : '',
            isEligible: false
          };
        }

        if (!isCapitalWithdrawAllowed(planId)) {
          return {
            index,
            isAvailable: false,
            timeRemaining: 0,
            status: 'not_eligible',
            isEligible: false
          };
        }

        try {
          const planInfo = planDetails[planId];
          if (!planInfo) {
            return {
              index,
              isAvailable: false,
              timeRemaining: 0,
              status: 'error',
              isEligible: false
            };
          }

          const capitalWithdrawTime = planInfo.capitalWithdrawTime;
          const unlockTime = inv.startTime.getTime() + (capitalWithdrawTime.toNumber() * 1000);
          const now = Date.now();
          
          return {
            index,
            isAvailable: now >= unlockTime,
            timeRemaining: Math.max(0, unlockTime - now),
            status: now >= unlockTime ? 'available' : 'locked',
            unlockTime: new Date(unlockTime),
            isEligible: true
          };
        } catch (error) {
          console.error(`Failed to calculate capital status for investment ${index}:`, error);
          return {
            index,
            isAvailable: false,
            timeRemaining: 0,
            status: 'error',
            isEligible: false
          };
        }
      })
    );
    setCapitalStatus(statusArray);
  };

  const loadInvestmentROIFromContract = async () => {
    if (!stakingContract || !userInfo) return;

    try {
      const roiCalculationPromises = userInvestments.map((investment, index) => 
        stakingContract.calculateROI(userInfo.id, index)
      );

      const contractROIResults = await Promise.all(roiCalculationPromises);
      const roiDataMap = {};

      contractROIResults.forEach((roiResult, index) => {
        roiDataMap[index] = ethers.utils.formatEther(roiResult);
      });

      setInvestmentROIData(roiDataMap);
    } catch (error) {
      console.error('Failed to load  data from contract:', error);
    }
  };

  const processROIWithdrawal = async (investmentIndex) => {
    if (!stakingContract || roiWithdrawalProcessing[investmentIndex]) return;

    const investment = userInvestments[investmentIndex];
    const planInfo = getPlanInfo(investment.planId);
    
    // Check ROI eligibility
    if (!canWithdrawROI(investment)) {
      if (isEndOnlyPlan(parseInt(investment.planId))) {
        addNotification(
          `This is an end-only plan. ${planInfo?.id === 1 ? '2x Payout' : 'Total accumulated rewards'} can only be withdrawn after the ${planInfo?.duration || 'investment period'} is complete.`,
          'warning'
        );
      } else {
        addNotification(' withdrawal not available for this investment.', 'error');
      }
      return;
    }

    const availableROI = parseFloat(investmentROIData[investmentIndex] || '0');
    if (availableROI <= 0) {
      addNotification('No  rewards available for withdrawal at this time.', 'info');
      return;
    }

    try {
      setROIWithdrawalProcessing(prev => ({ ...prev, [investmentIndex]: true }));
      
      addNotification(' withdrawal transaction submitted. Please wait for confirmation...', 'info');

      const withdrawalTransaction = await stakingContract.withdrawROI(investmentIndex);
      
      await withdrawalTransaction.wait();
      
      addNotification(
        `Successfully withdrew ${formatNumber(availableROI, 6)} USDT from ${planInfo?.name || 'Plan ' + investment.planId}!`, 
        'success'
      );
      
      // Refresh contract data
      await refreshData();
      await loadInvestmentROIFromContract();

    } catch (error) {
      console.error(' withdrawal transaction failed:', error);
      
      if (error.code === 4001) {
        addNotification('Transaction was cancelled by user.', 'warning');
      } else if (error.message.includes('No reward available')) {
        addNotification('No  rewards are currently available for withdrawal.', 'info');
      } else if (error.message.includes('Referral requirement not met')) {
        addNotification('Referral requirements must be maintained for  withdrawals from this plan.', 'warning');
      } else {
        addNotification('Reward withdrawal failed. Please try again or contact support if the issue persists.', 'error');
      }

    } finally {
      setROIWithdrawalProcessing(prev => ({ ...prev, [investmentIndex]: false }));
    }
  };

  const processCapitalWithdrawal = async (investmentIndex) => {
    if (!stakingContract || capitalWithdrawalProcessing[investmentIndex]) return;

    const investment = userInvestments[investmentIndex];
    const capitalInfo = capitalStatus[investmentIndex];
    const planInfo = getPlanInfo(investment.planId);
    
    // Check capital withdrawal eligibility
    if (!capitalInfo || !capitalInfo.isEligible) {
      addNotification('Capital withdrawal is not available for this plan.', 'warning');
      return;
    }

    if (!capitalInfo.isAvailable) {
      const timeRemaining = formatTimeRemaining(capitalInfo.timeRemaining);
      addNotification(`Capital is still locked. Available in: ${timeRemaining}`, 'info');
      return;
    }
    
    // Confirmation dialog
    const confirmationMessage = `Are you sure you want to withdraw your capital of ${formatNumber(investment.amount, 2)} USDT from ${planInfo?.name || 'Plan ' + investment.planId}?\n\nThis action will permanently close your investment and stop all future returns.`;
    
    if (!window.confirm(confirmationMessage)) return;

    try {
      setCapitalWithdrawalProcessing(prev => ({ ...prev, [investmentIndex]: true }));
      
      addNotification('Capital withdrawal transaction submitted. Please wait for confirmation...', 'info');

      const capitalTransaction = await stakingContract.withdrawCapital(investmentIndex);
      
      await capitalTransaction.wait();
      
      addNotification(
        `Successfully withdrew capital of ${formatNumber(investment.amount, 2)} USDT from ${planInfo?.name || 'Plan ' + investment.planId}!`, 
        'success'
      );
      
      // Refresh contract data
      await refreshData();
      await loadInvestmentROIFromContract();
      await calculateCapitalStatus();

    } catch (error) {
      console.error('Capital withdrawal transaction failed:', error);
      
      if (error.code === 4001) {
        addNotification('Transaction was cancelled by user.', 'warning');
      } else if (error.message.includes('Lock period not over')) {
        addNotification('Capital is still in lock period. Please wait for the unlock time.', 'warning');
      } else if (error.message.includes('Already withdrawn')) {
        addNotification('Capital has already been withdrawn from this investment.', 'info');
      } else if (error.message.includes('Capital withdrawal not allowed')) {
        addNotification('Capital withdrawal is not permitted for this plan type.', 'warning');
      } else {
        addNotification('Capital withdrawal failed. Please try again or contact support if the issue persists.', 'error');
      }

    } finally {
      setCapitalWithdrawalProcessing(prev => ({ ...prev, [investmentIndex]: false }));
    }
  };

  const formatTimeRemaining = (milliseconds) => {
    if (milliseconds <= 0) return 'Available now';
    
    const minutes = Math.floor(milliseconds / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    return `${minutes}m`;
  };

  const retrievePlanConfiguration = (planId) => {
    return PLANS.find(plan => plan.id === parseInt(planId)) || PLANS[0];
  };

  const determineInvestmentStatus = (investment) => {
    
    if (investment.capitalWithdrawn) return { status: 'Completed', color: '#10b981' };
    
    return { status: 'Active', color: '#3b82f6' };
  };

  const calculateInvestmentProgress = (investment) => {
    try {
      const planConfig = retrievePlanConfiguration(investment.planId);
      if (!planConfig || !investment.startTime) {
        return 0;
      }

      const currentTime = Date.now();
      let investmentStartTime;

      if (investment.startTime instanceof Date) {
        investmentStartTime = investment.startTime.getTime();
      } else if (typeof investment.startTime === 'number') {
        investmentStartTime = investment.startTime;
      } else if (typeof investment.startTime === 'string') {
        investmentStartTime = new Date(investment.startTime).getTime();
      } else {
        return 0;
      }

      if (isNaN(investmentStartTime) || investmentStartTime <= 0) {
        return 0;
      }

      const durationMs = planConfig.duration > 100 
        ? planConfig.duration * 24 * 60 * 60 * 1000
        : planConfig.duration * 30 * 24 * 60 * 60 * 1000;

      const investmentEndTime = investmentStartTime + durationMs;
      const elapsedTime = currentTime - investmentStartTime;
      const totalDuration = investmentEndTime - investmentStartTime;
      
      if (totalDuration <= 0) {
        return 0;
      }

      const progressPercentage = (elapsedTime / totalDuration) * 100;
      const finalProgress = Math.min(100, Math.max(0, progressPercentage));
      
      return isNaN(finalProgress) ? 0 : Math.round(finalProgress);
      
    } catch (error) {
      console.error('Error calculating investment progress:', error);
      return 0;
    }
  };

  const calculateTotalActiveInvestments = () => {
    return userInvestments
      .filter(inv => inv.isActive && !inv.capitalWithdrawn)
      .reduce((total, inv) => total + parseFloat(inv.amount), 0);
  };

  const calculateTotalWithdrawnAmount = () => {
    return userInvestments.reduce((total, inv) => total + parseFloat(inv.totalWithdrawn), 0);
  };

  const calculateTotalAvailableROI = () => {
    return Object.values(investmentROIData).reduce((total, roi) => total + parseFloat(roi), 0);
  };

  const formatDate = (dateInput) => {
    try {
      let date;
      if (dateInput instanceof Date) {
        date = dateInput;
      } else if (typeof dateInput === 'number') {
        date = new Date(dateInput);
      } else if (typeof dateInput === 'string') {
        date = new Date(dateInput);
      } else {
        return 'Invalid Date';
      }

      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }

      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  const getInvestmentDisplayInfo = (investment, index) => {
    const planId = parseInt(investment.planId);
    const planInfo = getPlanInfo(planId);
    const isEndOnly = isEndOnlyPlan(planId);
    const canWithdraw = canWithdrawROI(investment);
    const capitalInfo = capitalStatus[index];
    
    return {
      planInfo,
      isEndOnly,
      canWithdraw,
      capitalInfo,
      withdrawType: isEndOnly ? (planId === 1 ? '2x Payout' : 'Total Accumulated') : ' Regular Reward'
    };
  };

  if (!userInvestments || userInvestments.length === 0) {
    return (
      <div className="investments-list-container">
        <div className="investments-empty-state-card">
          <div className="investments-empty-state-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3v18h18V3"/>
              <path d="M8 8h8"/>
              <path d="M8 12h8"/>
              <path d="M8 16h8"/>
            </svg>
          </div>
          <h3 className="investments-empty-state-title">No Active Investments</h3>
          <p className="investments-empty-state-description">
            You haven't created any investments yet. Start investing to begin earning returns.
          </p>
          <div className="investments-empty-state-actions">
            <button className="investments-primary-action-button">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v20"/>
                <path d="M2 12h20"/>
              </svg>
              Create First Investment
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="investments-list-container">
      {/* Notifications */}
      <div className="investments-notifications">
        {notifications.map(notification => (
          <div 
            key={notification.id} 
            className={`investments-notification investments-${notification.type}-notification`}
          >
            <div className="notification-content">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {notification.type === 'success' && (
                  <>
                    <path d="M9 12l2 2 4-4"/>
                    <circle cx="12" cy="12" r="10"/>
                  </>
                )}
                {notification.type === 'error' && (
                  <>
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="15" y1="9" x2="9" y2="15"/>
                    <line x1="9" y1="9" x2="15" y2="15"/>
                  </>
                )}
                {notification.type === 'warning' && (
                  <>
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </>
                )}
                {notification.type === 'info' && (
                  <>
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="16" x2="12" y2="12"/>
                    <line x1="12" y1="8" x2="12.01" y2="8"/>
                  </>
                )}
              </svg>
              <span>{notification.message}</span>
            </div>
            <button
              className="notification-close"
              onClick={() => removeNotification(notification.id)}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {/* Header Section */}
      <div className="investments-list-header">
        <div className="investments-header-content">
          <h1 className="investments-page-title">Investment Portfolio</h1>
          <div className="investments-portfolio-metrics">
            <div className="investments-metric-item">
              <span className="investments-metric-label">Total Investments</span>
              <span className="investments-metric-value">{userInvestments.length}</span>
            </div>
            <div className="investments-metric-item">
              <span className="investments-metric-label">Active Positions</span>
              <span className="investments-metric-value">
                {userInvestments.filter(inv => inv.isActive && !inv.capitalWithdrawn).length}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Investments Grid */}
      <div className="investments-list-grid">
        {userInvestments.map((investment, index) => {
          const planConfiguration = retrievePlanConfiguration(investment.planId);
          const investmentStatus = determineInvestmentStatus(investment);
          const progressPercentage = calculateInvestmentProgress(investment);
          const availableROIAmount = investmentROIData[index] || '0';
          const hasAvailableROI = parseFloat(availableROIAmount) > 0;
          const displayInfo = getInvestmentDisplayInfo(investment, index);

          return (
            <div key={index} className="investments-card">
              {/* Card Header */}
              <div className="investments-card-header">
                <div className="investments-plan-information">
                  <h3 className="investments-plan-title">{planConfiguration.name}</h3>
                  <span className="investments-plan-identifier">Plan ID: {investment.planId}</span>
                  <span className="plan-type-info">{displayInfo.withdrawType}</span>
                </div>
                <div className="investments-status-indicator" style={{ backgroundColor: investmentStatus.color }}>
                  <span className="investments-status-text">{investmentStatus.status}</span>
                </div>
              </div>

              {/* Investment Information */}
              <div className="investments-information">
                <div className="investments-info-grid">
                  <div className="investments-info-item">
                    <span className="investments-info-label">Staked Amount</span>
                    <span className="investments-info-value investments-primary-value">{formatNumber(investment.amount, 2)} USDT</span>
                  </div>

                  <div className="investments-info-item">
                    <span className="investments-info-label">Reward Rate</span>
                    <span className="investments-info-value">{planConfiguration.roi}</span>
                  </div>

                  <div className="investments-info-item">
                    <span className="investments-info-label">Withdrawal Schedule</span>
                    <span className="investments-info-value">{planConfiguration.frequency}</span>
                  </div>

                  <div className="investments-info-item">
                    <span className="investments-info-label">Investment Date</span>
                    <span className="investments-info-value">{formatDate(investment.startTime)}</span>
                  </div>

                  <div className="investments-info-item">
                    <span className="investments-info-label">Last Withdrawal</span>
                    <span className="investments-info-value">{formatDate(investment.lastWithdraw)}</span>
                  </div>

                  <div className="investments-info-item">
                    <span className="investments-info-label">Total Withdrawn</span>
                    <span className="investments-info-value investments-success-value">{formatNumber(investment.totalWithdrawn, 2)} USDT</span>
                  </div>
                </div>
              </div>

              {/* Progress Tracking */}
              <div className="investments-progress-tracking">
                <div className="investments-progress-header">
                  <span className="investments-progress-title">Investment Progress</span>
                  <span className="investments-progress-value">{progressPercentage}%</span>
                </div>
                <div className="investments-progress-bar-container">
                  <div className="investments-progress-bar">
                    <div 
                      className="investments-progress-fill" 
                      style={{ width: `${progressPercentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="investments-progress-details">
                  <span className="investments-duration-info">
                    Duration: {planConfiguration.duration} {planConfiguration.duration > 100 ? 'days' : ''}
                  </span>
                </div>
              </div>

              {/* ROI Management */}
              <div className="investments-roi-management">
                <div className="investments-roi-display">
                  <span className="investments-roi-title">
                    Available {displayInfo.isEndOnly ? displayInfo.withdrawType : 'Rewards'}
                  </span>
                  <span className="investments-roi-value">{formatNumber(availableROIAmount, 6)} USDT</span>
                </div>
                
                {investment.isActive && !investment.capitalWithdrawn && (
                  <button
                    className={`investments-action-button investments-roi-button ${!displayInfo.canWithdraw || !hasAvailableROI ? 'disabled' : ''}`}
                    onClick={() => processROIWithdrawal(index)}
                    disabled={!displayInfo.canWithdraw || !hasAvailableROI || roiWithdrawalProcessing[index] || !userInfo?.roiEnabled}
                  >
                    {roiWithdrawalProcessing[index] ? (
                      <span className="investments-button-content">
                        <span className="investments-loading-indicator"></span>
                        Processing...
                      </span>
                    ) : (
                      <span className="investments-button-content">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="7,10 12,15 17,10"/>
                          <line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        Withdraw {displayInfo.isEndOnly ? displayInfo.withdrawType : 'Rewards'}
                      </span>
                    )}
                  </button>
                )}

                {/* End-only plan status */}
                {displayInfo.isEndOnly && !displayInfo.canWithdraw && (
                  <div className="end-only-status">
                    <span className="info-icon">⏰</span>
                    <span className="info-text">
                      {displayInfo.withdrawType} available after {displayInfo.planInfo?.duration}
                    </span>
                  </div>
                )}
              </div>

              {/* Capital Management - Only show if eligible */}
              {displayInfo.capitalInfo && displayInfo.capitalInfo.isEligible && (
                <div className="investments-capital-management">
                  <div className="investments-capital-status">
                    <span className="investments-capital-title">Capital Status</span>
                    <span className={`investments-capital-indicator ${investment.capitalWithdrawn ? 'withdrawn' : displayInfo.capitalInfo.isAvailable ? 'available' : 'locked'}`}>
                      {investment.capitalWithdrawn ? 'Withdrawn' : displayInfo.capitalInfo.isAvailable ? 'Available' : 'Locked'}
                    </span>
                  </div>

                  {displayInfo.capitalInfo.isAvailable && !investment.capitalWithdrawn && (
                    <button
                      className="investments-action-button investments-capital-button"
                      onClick={() => processCapitalWithdrawal(index)}
                      disabled={capitalWithdrawalProcessing[index]}
                    >
                      {capitalWithdrawalProcessing[index] ? (
                        <span className="investments-button-content">
                          <span className="investments-loading-indicator"></span>
                          Processing...
                        </span>
                      ) : (
                        <span className="investments-button-content">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="7,10 12,15 17,10"/>
                            <line x1="12" y1="15" x2="12" y2="3"/>
                          </svg>
                          Withdraw Capital
                        </span>
                      )}
                    </button>
                  )}

                  {displayInfo.capitalInfo.status === 'locked' && (
                    <div className="capital-locked-info">
                      <span className="lock-icon">🔒</span>
                      <span className="lock-text">
                        Capital available in: {formatTimeRemaining(displayInfo.capitalInfo.timeRemaining)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              
            </div>
          );
        })}
      </div>

      {/* Portfolio Summary */}
      <div className="investments-portfolio-summary">
        <div className="investments-summary-card">
          <h3 className="investments-summary-title">Portfolio Overview</h3>
          <div className="investments-summary-statistics">
            <div className="investments-summary-stat">
              <div className="investments-stat-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23"/>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                </svg>
              </div>
              <div className="investments-stat-content">
                <span className="investments-stat-amount">{formatNumber(calculateTotalActiveInvestments(), 2)} USDT</span>
                <span className="investments-stat-description">Active Investments</span>
              </div>
            </div>

            <div className="investments-summary-stat">
              <div className="investments-stat-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="22,12 18,12 15,21 9,3 6,12 2,12"/>
                </svg>
              </div>
              <div className="investments-stat-content">
                <span className="investments-stat-amount">{formatNumber(calculateTotalWithdrawnAmount(), 2)} USDT</span>
                <span className="investments-stat-description">Total Withdrawn</span>
              </div>
            </div>

            <div className="investments-summary-stat">
              <div className="investments-stat-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2v20"/>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                </svg>
              </div>
              <div className="investments-stat-content">
                <span className="investments-stat-amount">{formatNumber(calculateTotalAvailableROI(), 6)} USDT</span>
                <span className="investments-stat-description">Available Rewards</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestmentsList;